import { useEffect, useState } from 'react';
import { flotte, installation } from '../api.js';
import { ilYA, occupation, octets } from '../format.js';
import type { InstallationVue, SiteFlotte } from '../api.js';
import { DétailSite } from './DetailSite.js';

interface Props {
  organizationId: string;
  /** Rôle dans cette organisation : conditionne les actions proposées, pas seulement affichées. */
  rôle: string;
}

const LIBELLÉS_STATUT: Record<string, string> = {
  up: 'en marche',
  degraded: 'dégradé',
  down: 'en panne',
  unknown: 'inconnu',
};

/** Vue flotte : une carte par site, l'essentiel visible sans cliquer. */
export function Flotte({ organizationId, rôle }: Props) {
  const [sites, setSites] = useState<SiteFlotte[] | null>(null);
  /**
   * Installations en cours ou en attente de DNS.
   *
   * Elles n'ont pas encore de site à montrer — ou en ont un qui n'est pas joignable — et rien ne les
   * signalait donc. Une installation qui attend ses enregistrements DNS depuis trois jours doit se
   * voir : c'est le seul endroit où l'on regarde tous les matins.
   */
  const [enCours, setEnCours] = useState<InstallationVue[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [siteOuvert, setSiteOuvert] = useState<string | null>(null);

  useEffect(() => {
    let annulé = false;
    const charger = async () => {
      try {
        const { status, data } = await flotte.lister(organizationId);
        if (annulé) return;
        if (status >= 400) {
          setErreur(data.error ?? 'Chargement impossible');
          return;
        }
        setSites(data.sites);
        const encours = await installation.actives(organizationId);
        if (!annulé && encours.status === 200) setEnCours(encours.data.installations);
      } catch {
        if (!annulé) setErreur('Service injoignable');
      }
    };
    void charger();
    // Rafraîchissement régulier : les sondes tournent en arrière-plan, la vue doit suivre sans
    // qu'on ait à recharger la page.
    const minuterie = setInterval(() => void charger(), 60_000);
    return () => {
      annulé = true;
      clearInterval(minuterie);
    };
  }, [organizationId]);

  if (siteOuvert !== null) {
    return (
      <DétailSite
        organizationId={organizationId}
        siteId={siteOuvert}
        rôle={rôle}
        onRetour={() => setSiteOuvert(null)}
      />
    );
  }

  if (erreur !== null) return <p className="erreur">{erreur}</p>;
  if (sites === null) return <p className="aide">Chargement de la flotte…</p>;

  const bandeau =
    enCours.length === 0 ? null : (
      <div className="carte-interne">
        <h3>Installations en cours</h3>
        <ul className="liste-suite">
          {enCours.map((inst) => (
            <li key={inst.id}>
              <strong>{inst.siteName}</strong> — {inst.url}
              {inst.status === 'dns'
                ? ' : en attente des enregistrements DNS'
                : inst.status === 'echouee' || inst.status === 'interrompue'
                  ? ' : arrêtée, reprise possible'
                  : ' : installation en cours'}
            </li>
          ))}
        </ul>
        <p className="aide">
          Reprends l’assistant d’ajout de site pour en suivre le détail : il retrouve l’installation
          là où elle en est.
        </p>
      </div>
    );

  if (sites.length === 0) {
    return (
      <>
        {bandeau}
        <p className="aide">Aucun site enregistré pour le moment.</p>
      </>
    );
  }

  return (
    <>
      {bandeau}
      <div className="flotte">
        {sites.map((site) => (
          <button
            key={site.id}
            type="button"
            className={`site-carte site-carte--${site.currentStatus}`}
            onClick={() => setSiteOuvert(site.id)}
          >
            <header>
              <span className="pastille" aria-hidden="true" />
              <span className="site-nom">{site.name}</span>
              <span className="site-statut">{LIBELLÉS_STATUT[site.currentStatus]}</span>
            </header>
            {site.operationInProgress !== null && (
              <p className="site-operation">opération en cours : {site.operationInProgress}</p>
            )}
            <dl>
              <div>
                <dt>Ghost</dt>
                <dd>{site.ghostVersionDetected ?? '—'}</dd>
              </div>
              <div>
                <dt>Disque</dt>
                <dd>
                  {occupation(site.diskFreeBytes, site.diskTotalBytes)}{' '}
                  <small>({octets(site.diskFreeBytes)} libres)</small>
                </dd>
              </div>
              <div>
                <dt>Latence</dt>
                <dd>{site.responseTimeMs === null ? '—' : `${String(site.responseTimeMs)} ms`}</dd>
              </div>
              <div>
                <dt>Sauvegarde</dt>
                <dd>{ilYA(site.lastBackupAt)}</dd>
              </div>
              <div>
                <dt>Mises à jour</dt>
                <dd>{site.updatePolicy}</dd>
              </div>
              <div>
                <dt>Sondé</dt>
                <dd>{ilYA(site.lastCheckedAt)}</dd>
              </div>
            </dl>
          </button>
        ))}
      </div>
    </>
  );
}
